import { Toast as KobalteToast, toaster } from '@kobalte/core/toast'
import { IconX } from '@tabler/icons-solidjs'
import { createEffect, type JSX, onCleanup, onMount, Show, splitProps } from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv, type VariantProps } from '../lib/tv.ts'

/**
 * Toasts are INK, matching the group's black controls. A dark slab over a light page reads
 * instantly and cannot be mistaken for another card — which is exactly what a white box with a
 * thin coloured border was doing.
 *
 * Status is carried by a dot, not by the surface: at two variants a whole coloured panel is
 * shouting, while a 6px dot next to white text is unambiguous and calm.
 *
 * Every toast is the SAME WIDTH. Content-width pills, right-aligned, read as scattered chips
 * rather than as one stack — the ragged left edge gives the eye nothing to follow, so three
 * notifications look like three unrelated things. A shared left edge is most of what makes a
 * pile of toasts legible as a pile.
 *
 * POSITION is owned entirely by the stack layout in ToastRegion, which writes an inline
 * `transform` on every card. Nothing here may set a transform — not the enter keyframe, not the
 * swipe classes — or the layout is overwritten and the pile falls apart.
 *
 * ALIGNMENT — the row is `items-start` so a two-line message keeps its dot and its × on the
 * FIRST line rather than floating to the vertical middle of the block. That only works if the
 * dot and the button are centred inside a box the height of one line: hard-coding a margin
 * (`mt-[0.4rem]`) silently goes out of true the moment the type scale moves, which is exactly
 * what happened when body text went from 14px to 13px.
 */
const toast = tv({
  slots: {
    root: [
      'pointer-events-auto absolute right-0 bottom-0 flex w-full items-start gap-2.5',
      'rounded-control bg-ink py-2 pr-2 pl-3 text-body text-text-inverse shadow-overlay',
      // Cards shrink toward the bottom edge, so a card behind the front one peeks out at the TOP,
      // which is the direction the eye is already travelling.
      'origin-bottom will-change-transform',
      'transition-[transform,opacity] duration-(--duration-overlay) ease-out',
      // Kobalte drives these through the toast lifecycle. Opacity only — see the note above.
      'data-[opened]:animate-[toast-in_200ms_var(--ease-out)]',
      'data-[closed]:animate-[toast-out_130ms_var(--ease-out)]',
      'data-[swipe=end]:animate-[toast-out_130ms_var(--ease-out)]',
    ],
    /** One line box tall, so the dot centres on the first line at any type size. */
    indicatorSlot: 'flex h-5 shrink-0 items-center',
    indicator: 'size-1.5 rounded-pill',
    title: 'min-w-0 flex-1 font-medium',
    close: [
      'flex h-5 w-5 shrink-0 items-center justify-center rounded-[0.25rem]',
      'text-text-inverse/55 transition-colors duration-(--duration-press) ease-out',
      'hover:bg-text-inverse/15 hover:text-text-inverse',
      'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-text-inverse',
    ],
  },
  variants: {
    variant: {
      success: { indicator: 'bg-status-done' },
      error: { indicator: 'bg-status-critical' },
    },
  },
  defaultVariants: { variant: 'success' },
})

export interface ToastProps extends VariantProps<typeof toast> {
  toastId: number
  message: string
  children?: JSX.Element
  closable?: boolean
  persistent?: boolean
}

/**
 * Ids of the toasts currently mounted, oldest first — a Set keeps insertion order.
 *
 * Maintained by the component itself rather than by a timer, because `pauseOnInteraction` can
 * extend a toast's life past its duration and any clock-based guess would then drift.
 */
const liveToasts = new Set<number>()
const persistentToasts = new Set<number>()

/**
 * How many are on screen at once. Beyond this the OLDEST is dismissed — see `showToast`.
 */
const MAX_VISIBLE = 3

export const Toast = (props: ToastProps) => {
  const [local] = splitProps(props, [
    'toastId',
    'message',
    'variant',
    'children',
    'closable',
    'persistent',
  ])
  const styles = () => toast({ variant: local.variant })

  // Registered inside an effect, not at setup: reading `local.toastId` once would freeze it, and
  // the cleanup would then try to release an id the toast no longer has. createEffect's own
  // cleanup runs both before a re-run and on disposal, so the set can never leak an entry.
  createEffect(() => {
    const id = local.toastId
    liveToasts.add(id)
    if (local.persistent) persistentToasts.add(id)
    onCleanup(() => {
      liveToasts.delete(id)
      persistentToasts.delete(id)
    })
  })

  return (
    <KobalteToast toastId={local.toastId} persistent={local.persistent} class={styles().root()}>
      <span class={styles().indicatorSlot()} aria-hidden="true">
        <span class={styles().indicator()} />
      </span>
      <div class={styles().title()}>
        <KobalteToast.Title>{local.message}</KobalteToast.Title>
        {local.children}
      </div>
      <Show when={local.closable !== false}>
        <KobalteToast.CloseButton class={styles().close()} aria-label="Закрити">
          <IconX size={12} strokeWidth={1.5} aria-hidden="true" />
        </KobalteToast.CloseButton>
      </Show>
    </KobalteToast>
  )
}

/**
 * One toast per call, always.
 *
 * An earlier version collapsed repeats of the same message, on the theory that three copies of
 * "Не вдалося зберегти зміни" say nothing the first one didn't. That is wrong here: two saves
 * that both succeeded are two events, and if the second one produces no visible feedback the
 * operator cannot tell whether it registered. In a system whose whole job is recording what
 * happened, feedback confirms THIS action — never a previous one that happened to read the same.
 */
export interface ToastAction {
  label: string
  onClick: () => void
}

export const showToast = (
  message: string,
  variant: NonNullable<ToastProps['variant']> = 'success',
  action?: ToastAction,
): number => {
  /**
   * Newest wins. Kobalte's own `limit` QUEUES the overflow instead of dropping it: click save
   * twenty times and the twentieth confirmation arrives about a minute later, long after the
   * screen has moved on. A notification is only useful at the moment it describes — one that
   * shows up a minute late is not late information, it is wrong information.
   *
   * So the oldest is dismissed here, BEFORE showing, and the count is capped by us rather than
   * by the region. Ids are removed from the set eagerly so this loop terminates whether or not
   * the exit animation (and therefore the component's cleanup) has finished.
   */
  while (liveToasts.size >= MAX_VISIBLE) {
    const oldest = [...liveToasts].find((id) => !persistentToasts.has(id))
    if (oldest === undefined) break
    liveToasts.delete(oldest)
    toaster.dismiss(oldest)
  }

  return toaster.show((props) => (
    <Toast toastId={props.toastId} message={message} variant={variant}>
      <Show when={action}>
        {(current) => (
          <button
            type="button"
            class="mt-0.5 font-medium text-text-inverse/80 underline underline-offset-2 hover:text-text-inverse focus-visible:outline-2 focus-visible:outline-text-inverse"
            onClick={() => {
              toaster.dismiss(props.toastId)
              current().onClick()
            }}
          >
            {current().label}
          </button>
        )}
      </Show>
    </Toast>
  ))
}

export type ToastRegionProps = JSX.HTMLAttributes<HTMLDivElement>

/** How much of each card behind the front one stays visible, in px. */
const COLLAPSED_PEEK = 9
/** How much smaller each card gets per step back. Subtle — 0.1 looks like a receipt printer. */
const COLLAPSED_SCALE_STEP = 0.045
/** Gap between cards once the pile is fanned out. */
const EXPANDED_GAP = 8

/**
 * Positions the pile.
 *
 * Sonner-style stacking cannot be done in CSS alone: fanning out means translating each card by
 * the summed heights of every card in front of it, and CSS has no way to read a sibling's size.
 * A version with fixed offsets works only while every toast is exactly one line, and jumps the
 * moment one wraps — which, with Ukrainian strings running ~30% longer than English, is the
 * normal case rather than the edge case.
 *
 * So the geometry is measured. This walks the list once per change and writes an inline
 * transform; the CSS transition on the card animates between the two states.
 *
 * Cards mid-exit are skipped: they still sit in the DOM until their animation ends, and counting
 * them would make the survivors shuffle down and back as each one leaves.
 */
const layoutStack = (list: HTMLElement, expanded: boolean): void => {
  const cards = ([...list.children] as HTMLElement[]).filter(
    (card) => !card.hasAttribute('data-closed'),
  )

  let offset = 0

  /* Walked NEWEST FIRST — the newest card is the anchor at translateY(0) in both states, and
     everything older is pushed up behind it. Walking oldest-first instead accumulates the fan
     from the wrong end: collapsed keeps the newest at the bottom, expanded moves it to the top,
     and the pile visibly turns itself over as it opens. */
  for (let index = cards.length - 1; index >= 0; index -= 1) {
    const card = cards[index]
    if (!card) continue
    // 0 is the newest card, at the front of the pile.
    const depth = cards.length - 1 - index

    if (expanded) {
      card.style.transform = `translateY(${-offset}px) scale(1)`
      offset += card.offsetHeight + EXPANDED_GAP
    } else {
      const scale = 1 - depth * COLLAPSED_SCALE_STEP
      card.style.transform = `translateY(${-depth * COLLAPSED_PEEK}px) scale(${scale})`
    }

    card.style.opacity = depth >= MAX_VISIBLE ? '0' : '1'
    card.style.zIndex = String(MAX_VISIBLE - depth)
    // Only the front card is interactive while collapsed — the ones behind it are 9px slivers,
    // and a close button you cannot see is a close button you hit by accident.
    card.style.pointerEvents = expanded || depth === 0 ? 'auto' : 'none'
  }

  // The cards are absolutely positioned, so the list has no height of its own — and a container
  // with no height receives no pointerenter, which would leave the pile permanently collapsed.
  const front = cards.at(-1)
  // Expanded: the accumulator ran one gap past the last card, so take it back off.
  list.style.height = expanded
    ? `${Math.max(offset - EXPANDED_GAP, 0)}px`
    : `${(front?.offsetHeight ?? 0) + Math.min(cards.length - 1, MAX_VISIBLE - 1) * COLLAPSED_PEEK}px`
}

export const ToastRegion = (props: ToastRegionProps) => {
  const [local, rest] = splitProps(props, ['class'])
  let mount!: HTMLDivElement

  onMount(() => {
    /* Everything below hangs off `mount`, a plain div we own, and resolves the <ol> LAZILY on
       every pass.
       Kobalte does not render the list element until the first toast arrives, so looking it up
       once during onMount finds nothing. A version that did that, and returned early, disabled
       the stack permanently — silently, because the code that would have reported a problem is
       the code that never ran. Observing a container that always exists removes the ordering
       question entirely. */
    let expanded = false

    const relayout = (): void => {
      const list = mount.querySelector('ol')
      if (list) layoutStack(list, expanded)
    }

    /* Height changes matter as much as arrivals: a two-line error changes every offset above it,
       and web fonts land after first paint. One observer, re-pointed at the cards on each change. */
    const sizes = new ResizeObserver(relayout)
    const contents = new MutationObserver(() => {
      sizes.disconnect()
      for (const card of mount.querySelectorAll('ol > li')) sizes.observe(card)
      relayout()
    })

    contents.observe(mount, {
      childList: true,
      subtree: true,
      attributes: true,
      // Only `data-closed`, so the inline styles written by layoutStack cannot retrigger this.
      attributeFilter: ['data-closed'],
    })
    relayout()

    const setExpanded = (next: boolean) => {
      expanded = next
      relayout()
    }
    const onEnter = () => setExpanded(true)
    const onLeave = () => setExpanded(false)

    mount.addEventListener('pointerenter', onEnter)
    mount.addEventListener('pointerleave', onLeave)
    // Keyboard users never fire pointerenter; without this the pile is unreadable by tabbing.
    mount.addEventListener('focusin', onEnter)
    mount.addEventListener('focusout', onLeave)

    onCleanup(() => {
      sizes.disconnect()
      contents.disconnect()
      mount.removeEventListener('pointerenter', onEnter)
      mount.removeEventListener('pointerleave', onLeave)
      mount.removeEventListener('focusin', onEnter)
      mount.removeEventListener('focusout', onLeave)
    })
  })

  return (
    <KobalteToast.Region
      {...rest}
      aria-label="Сповіщення ({hotkey})"
      /**
       * Every one of these is a correction to a Kobalte default that misbehaves in this app:
       *
       * `pauseOnPageIdle` defaults to TRUE — the dismiss timer stops whenever the document loses
       * focus. Alt-tab away with three toasts up and they are still there on return, permanently,
       * because the limit is full and nothing new can ever be shown. That is the "only ever three"
       * behaviour. In an operations tool a notification is not a thing you must come back to.
       *
       * `limit` is set ABOVE our own cap on purpose. Kobalte queues anything over its limit, and
       * a toast that is mid-exit still occupies a slot for the length of the animation — so a
       * region limit equal to the cap would make every replacement queue for 130ms and stutter.
       * `showToast` is what actually bounds the count, by dismissing the oldest.
       *
       * `duration` is 4.5s: long enough to read a Ukrainian sentence, which runs about 30% longer
       * than its English equivalent, short enough not to sit over the work.
       *
       * `topLayer` puts the region in the browser's top layer so toasts are visible above an open
       * <dialog>. Without it a confirmation fired from inside a modal renders behind its backdrop.
       */
      duration={4500}
      limit={MAX_VISIBLE + 2}
      pauseOnInteraction
      pauseOnPageIdle={false}
      topLayer
      /* Bottom-right, not top-right: the top-right corner is where this app puts primary page
         actions, and toasts were landing on top of them. Fixed width, so the toasts inside share
         both edges and stack as a column instead of a ragged pile of chips. */
      class={cn('fixed z-50 w-[min(22rem,calc(100vw-2rem))]', local.class)}
      style={{
        right: 'var(--toast-inline-inset, 1rem)',
        top: 'var(--toast-block-start, auto)',
        bottom: 'var(--toast-inset, 1rem)',
      }}
    >
      {/* Newest last — the front of the pile, nearest the corner the eye is already drawn to.
          `transition-[height]` so the container grows and shrinks with the fan rather than
          snapping, which would clip the cards mid-animation. */}
      <div ref={mount}>
        <KobalteToast.List class="relative outline-none transition-[height] duration-(--duration-overlay) ease-out" />
      </div>
    </KobalteToast.Region>
  )
}
